import { CloseCircleOutlined, LoadingOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Form, FormItemProps, message, Row, Spin, Typography, Upload } from "antd";
import React, { CSSProperties } from "react";
import { Controller, UseControllerProps } from "react-hook-form";
import { imageFormats } from "../../../utils/helper";
import { SectionLoader } from "../../atoms/SectionLoader/SectionLoader";
import { useFetchDocument } from "./useFetchDocument";
import { useUploadDocument } from "./useUploadDocument";

// Default allow list across the app: common document/image types.
// Individual screens should override via `allowedTypes` / `acceptExtensions` when they need something narrower (e.g. CSV-only).
const ACCEPTED_FILE_EXTENSIONS = ".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp";
const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

type UploadDocumentProps = {
  fieldname: string;
  label?: string;
  control: any;
  rules?: UseControllerProps["rules"];
  isRequired?: boolean;
  validate?: FormItemProps["validateStatus"];
  validMessage?: string;
  disabled?: boolean;
  maxSize?: number;
  allowedTypes?: string[];
  acceptExtensions?: string;
  tooltip?: string;
  styles?: CSSProperties;
  withPreview?: boolean;
};

export const UploadDocument = (props: UploadDocumentProps) => {
  const {
    fieldname,
    label = "Document",
    control,
    rules,
    isRequired = false,
    validate,
    validMessage,
    disabled = false,
    maxSize = 10,
    allowedTypes = ALLOWED_FILE_TYPES,
    acceptExtensions = ACCEPTED_FILE_EXTENSIONS,
    tooltip,
    styles,
    withPreview = true,
  } = props;

  const uploadRowProps = {
    height: withPreview ? "7.5rem" : "2.5rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px dashed gray",
    transition: "all 0.3s ease-in-out",
    borderRadius: "0.5rem",
    ...styles,
  };

  const viewFile = (url) => {
    url && window?.open(url, "_blank");
  };

  return (
    <Controller
      name={fieldname}
      control={control}
      defaultValue={null}
      rules={rules}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const { attachment, isLoading, clearAttachment } = useFetchDocument(value?.documentId);
        const { isFileUploading, uploadProps } = useUploadDocument({
          maxSize,
          allowedTypes,
          acceptExtensions,
          disabled: disabled || isLoading,
          onSuccess: onChange,
        });

        const handleRemoveFile = () => {
          clearAttachment();
          onChange(null);
          message.info("File removed");
        };

        return (
          <Form.Item
            label={label}
            required={isRequired}
            validateStatus={error ? "error" : validate}
            help={error ? error.message : validMessage}
            tooltip={tooltip}
          >
            <div style={{ transition: "opacity 0.3s ease-in-out", opacity: isLoading ? 0.6 : 1 }}>
              {isFileUploading || isLoading ? (
                <Row justify="center" style={{ ...uploadRowProps }}>
                  <Spin indicator={<LoadingOutlined spin />} />
                </Row>
              ) : attachment?.document?.label ? (
                <Row
                  justify="center"
                  style={{ ...uploadRowProps, flexDirection: "column", position: "relative", padding: "10px" }}
                >
                  <Button
                    type="text"
                    icon={<CloseCircleOutlined />}
                    onClick={handleRemoveFile}
                    disabled={disabled}
                    style={{ position: "absolute", top: "3px", right: "3px", color: "#ff4d4f" }}
                  />

                  {withPreview && imageFormats?.includes(attachment?.document?.fileType?.toLocaleLowerCase()) ? (
                    isLoading ? (
                      <SectionLoader containerStyle={{ height: "3rem" }} />
                    ) : (
                      <img
                        onClick={() => viewFile(attachment?.document?.fileUrl)}
                        src={attachment?.document?.fileUrl}
                        title="Click to view"
                        alt="attachment"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          cursor: "pointer",
                        }}
                      />
                    )
                  ) : (
                    <div
                      onClick={() => viewFile(attachment?.document?.fileUrl)}
                      title="Click to view"
                      style={{ cursor: "pointer" }}
                    >
                      <Typography.Text strong style={{ wordBreak: "break-all", textAlign: "center", maxWidth: "90%" }}>
                        {attachment?.document?.label}
                      </Typography.Text>
                    </div>
                  )}
                </Row>
              ) : (
                <Upload {...uploadProps}>
                  <div
                    style={{
                      ...uploadRowProps,
                      cursor: disabled ? "not-allowed" : "pointer",
                      opacity: disabled ? 0.5 : 1,
                    }}
                  >
                    <span style={{ textAlign: "center", padding: "0 10px" }}>
                      <UploadOutlined /> &nbsp; Click to Upload ({acceptExtensions.replace(/\./g, "").toUpperCase()})
                    </span>
                  </div>
                </Upload>
              )}
            </div>
          </Form.Item>
        );
      }}
    />
  );
};
